/**
 * Content area for the Schedule Interview wizard.
 * Renders mode-driven step tabs/stepper with panels.
 */
import { useMemo } from 'react';
import { Flex, Text } from '@chakra-ui/react';
import { Tabs, type TabItem } from '@kula-ai/components';
import type { ScheduleModeConfig, ScheduleStepProps } from './schedule.types';
import { DetailsStep } from './steps/details/DetailsStep';
import { CommunicationStep } from './steps/communication/CommunicationStep';
import { ScorecardStep } from './steps/scorecard/ScorecardStep';
import { SlotSelectionEmailStep } from './steps/slotSelectionEmail/SlotSelectionEmailStep';

const STEP_COMPONENTS: Record<string, React.ComponentType<any>> = {
  details: DetailsStep,
  communication: CommunicationStep,
  scorecard: ScorecardStep,
  slotSelectionEmail: SlotSelectionEmailStep,
};

const PANEL_CLASS =
  '!mt-0 border-t border-secondary-100 flex min-h-0 flex-1 flex-col overflow-hidden ' +
  'data-[state=inactive]:!flex-none';

interface ScheduleContentProps {
  config: ScheduleModeConfig;
  stepProps: Omit<ScheduleStepProps, 'interviewPlan'>;
  interviewPlan?: Record<string, unknown>;
  candidateName?: string;
  getStepRef: (_stepKey: string) => React.RefObject<any>;
  activeStep: string;
  onStepChange: (_value: string) => void;
}

export const ScheduleContent = ({
  config,
  stepProps,
  interviewPlan,
  candidateName,
  getStepRef,
  activeStep,
  onStepChange,
}: ScheduleContentProps) => {
  const tabItems = useMemo(
    (): TabItem[] =>
      config.steps.map((step) => {
        const StepComponent = STEP_COMPONENTS[step.key];
        const stepRef = getStepRef(step.key);
        return {
          label: step.label,
          value: step.key,
          panel: StepComponent ? (
            <StepComponent
              ref={stepRef}
              {...stepProps}
              {...(step.key === 'details'
                ? { interviewPlan, candidateName }
                : {})}
            />
          ) : (
            <Text>Step under developement: {step.key}</Text>
          ),
        };
      }),
    [config.steps, stepProps, getStepRef, interviewPlan, candidateName]
  );

  const isStepper = config.navigationStyle !== 'tabs';

  return (
    <Flex flex="1" flexDirection="column" minH={0}>
      <Tabs.Root
        variant={isStepper ? 'stepper' : 'line'}
        items={tabItems}
        value={activeStep}
        onValueChange={onStepChange}
        preservePanels
        className="flex h-full min-h-0 w-full flex-1 flex-col"
        panelClassName={`${PANEL_CLASS} ${
          activeStep === 'details' ? 'p-0' : 'p-4'
        }`}
        tabListWrapperClassName={
          isStepper
            ? 'shrink-0 max-w-[600px] !border-b-0 px-4 pt-1 pb-3'
            : 'shrink-0 px-4'
        }
      />
    </Flex>
  );
};
